/**
 * Web Vitals Performance Monitoring
 *
 * Tracks Core Web Vitals metrics:
 * - LCP (Largest Contentful Paint): Loading performance
 * - INP (Interaction to Next Paint): Interactivity
 * - CLS (Cumulative Layout Shift): Visual stability
 * - FCP (First Contentful Paint): Initial render
 * - TTFB (Time to First Byte): Server response time
 *
 * @see https://web.dev/vitals/
 */

import { onCLS, onINP, onLCP, onFCP, onTTFB, type Metric } from 'web-vitals';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Metric rating
 */
export type MetricRating = 'good' | 'needs-improvement' | 'poor';

/**
 * Metric name
 */
export type MetricName = 'LCP' | 'INP' | 'CLS' | 'FCP' | 'TTFB';

/**
 * Performance thresholds
 */
export interface MetricThreshold {
    good: number;
    needsImprovement: number;
}

/**
 * Formatted metric data
 */
export interface FormattedMetric {
    name: string;
    value: number;
    rating: MetricRating;
    delta: number;
    id: string;
    navigationType: string | undefined;
    timestamp: number;
}

/**
 * Web Vitals initialization options
 */
export interface WebVitalsOptions {
    onMetric?: (metric: FormattedMetric) => void;
    analyticsEndpoint?: string | null;
    debug?: boolean;
}

/**
 * Long task entry
 */
export interface LongTaskEntry {
    duration: number;
    startTime: number;
    name: string;
}

/**
 * Performance timing metrics
 */
export interface PerformanceTiming {
    dnsLookup: number;
    tcpConnection: number;
    serverResponse: number;
    domParsing: number;
    domContentLoaded: number;
    fullLoad: number;
}

/**
 * Navigation metrics
 */
export interface NavigationMetrics {
    type: string | undefined;
    redirectCount: number | undefined;
    transferSize: number | undefined;
    encodedBodySize: number | undefined;
    decodedBodySize: number | undefined;
}

/**
 * Resource metrics
 */
export interface ResourceMetrics {
    total: number;
    totalSize: number;
    byType: Record<string, number>;
}

/**
 * Memory metrics (Chrome only)
 */
export interface MemoryMetrics {
    usedJSHeapSize: number;
    totalJSHeapSize: number;
    jsHeapSizeLimit: number;
}

/**
 * Complete performance report
 */
export interface PerformanceReport {
    timing?: PerformanceTiming;
    navigation?: NavigationMetrics;
    resources?: ResourceMetrics;
    memory?: MemoryMetrics;
}

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Performance thresholds based on Google's Core Web Vitals
 */
const THRESHOLDS: Record<MetricName, MetricThreshold> = {
    LCP: { good: 2500, needsImprovement: 4000 }, // ms
    INP: { good: 200, needsImprovement: 500 },   // ms
    CLS: { good: 0.1, needsImprovement: 0.25 },  // score
    FCP: { good: 1800, needsImprovement: 3000 }, // ms
    TTFB: { good: 800, needsImprovement: 1800 }, // ms
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get rating for a metric value
 * @param name - Metric name
 * @param value - Metric value
 * @returns Rating
 */
function getRating(name: string, value: number): MetricRating {
    const threshold = THRESHOLDS[name as MetricName];
    if (!threshold) return 'good';

    if (value <= threshold.good) return 'good';
    if (value <= threshold.needsImprovement) return 'needs-improvement';
    return 'poor';
}

/**
 * Format metric for logging
 * @param metric - Web Vitals metric object
 * @returns Formatted metric data
 */
function formatMetric(metric: Metric): FormattedMetric {
    const rating = getRating(metric.name, metric.value);
    return {
        name: metric.name,
        value: metric.value,
        rating,
        delta: metric.delta,
        id: metric.id,
        navigationType: metric.navigationType,
        timestamp: Date.now(),
    };
}

/**
 * Default handler that logs to console in development
 * @param metric - Formatted metric data
 */
function defaultHandler(metric: FormattedMetric): void {
    const colors: Record<MetricRating, string> = {
        good: '\x1b[32m',      // green
        'needs-improvement': '\x1b[33m', // yellow
        poor: '\x1b[31m',      // red
    };
    const reset = '\x1b[0m';
    const color = colors[metric.rating] || reset;

    if (import.meta.env.DEV) {
        console.log(
            `${color}[Web Vitals] ${metric.name}: ${metric.value.toFixed(2)} (${metric.rating})${reset}`
        );
    }
}

/**
 * Send metrics to analytics endpoint
 * @param metric - Formatted metric data
 * @param endpoint - Analytics endpoint URL
 */
async function sendToAnalytics(metric: FormattedMetric, endpoint: string): Promise<void> {
    try {
        // Use sendBeacon for reliability during page unload
        if (navigator.sendBeacon) {
            navigator.sendBeacon(
                endpoint,
                JSON.stringify(metric)
            );
        } else {
            // Fallback to fetch
            await fetch(endpoint, {
                method: 'POST',
                body: JSON.stringify(metric),
                headers: { 'Content-Type': 'application/json' },
                keepalive: true,
            });
        }
    } catch {
        // Silently fail - analytics failures shouldn't impact the app
    }
}

// ============================================================================
// MAIN FUNCTIONS
// ============================================================================

/**
 * Initialize Web Vitals monitoring
 *
 * @param options - Configuration options
 *
 * @example
 * // Basic usage (logs to console in dev)
 * initWebVitals();
 *
 * @example
 * // With custom handler
 * initWebVitals({
 *   onMetric: (metric) => {
 *     myAnalytics.track('web-vital', metric);
 *   }
 * });
 *
 * @example
 * // With analytics endpoint
 * initWebVitals({
 *   analyticsEndpoint: '/api/vitals'
 * });
 */
export function initWebVitals(options: WebVitalsOptions = {}): void {
    const {
        onMetric = defaultHandler,
        analyticsEndpoint = null,
        debug = import.meta.env.DEV,
    } = options;

    const handleMetric = (metric: Metric): void => {
        const formatted = formatMetric(metric);

        // Call custom handler
        if (onMetric) {
            onMetric(formatted);
        }

        // Send to analytics if endpoint provided
        if (analyticsEndpoint) {
            sendToAnalytics(formatted, analyticsEndpoint);
        }

        // Debug logging
        if (debug) {
            defaultHandler(formatted);
        }
    };

    // Register all Web Vitals observers
    onCLS(handleMetric);
    onINP(handleMetric);
    onLCP(handleMetric);
    onFCP(handleMetric);
    onTTFB(handleMetric);

    if (debug) {
        console.log('[Web Vitals] Monitoring initialized');
    }
}

/**
 * Get a performance report of all collected metrics
 * Useful for displaying in a debug panel
 *
 * @returns Performance report
 */
export async function getPerformanceReport(): Promise<PerformanceReport> {
    const metrics: PerformanceReport = {};

    // Collect current performance timing
    if (typeof window !== 'undefined' && window.performance) {
        const timing = performance.timing || ({} as PerformanceTiming);
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;

        metrics.timing = {
            dnsLookup: timing.domainLookupEnd - timing.domainLookupStart,
            tcpConnection: timing.connectEnd - timing.connectStart,
            serverResponse: timing.responseEnd - timing.requestStart,
            domParsing: timing.domInteractive - timing.responseEnd,
            domContentLoaded: timing.domContentLoadedEventEnd - timing.navigationStart,
            fullLoad: timing.loadEventEnd - timing.navigationStart,
        };

        if (navigation) {
            metrics.navigation = {
                type: navigation.type,
                redirectCount: navigation.redirectCount,
                transferSize: navigation.transferSize,
                encodedBodySize: navigation.encodedBodySize,
                decodedBodySize: navigation.decodedBodySize,
            };
        }

        // Resource timing
        const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
        metrics.resources = {
            total: resources.length,
            totalSize: resources.reduce((sum, r) => sum + (r.transferSize || 0), 0),
            byType: resources.reduce((acc: Record<string, number>, r) => {
                const type = r.initiatorType || 'other';
                acc[type] = (acc[type] || 0) + 1;
                return acc;
            }, {}),
        };

        // Memory (Chrome only)
        interface PerformanceWithMemory extends Performance {
            memory?: {
                usedJSHeapSize: number;
                totalJSHeapSize: number;
                jsHeapSizeLimit: number;
            };
        }
        const perfWithMemory = performance as PerformanceWithMemory;
        if (perfWithMemory.memory) {
            metrics.memory = {
                usedJSHeapSize: perfWithMemory.memory.usedJSHeapSize,
                totalJSHeapSize: perfWithMemory.memory.totalJSHeapSize,
                jsHeapSizeLimit: perfWithMemory.memory.jsHeapSizeLimit,
            };
        }
    }

    return metrics;
}

/**
 * Create a performance observer for long tasks
 * Helps identify JavaScript that blocks the main thread
 *
 * @param callback - Called when a long task is detected
 * @returns The observer instance
 */
export function observeLongTasks(callback: (entry: LongTaskEntry) => void): PerformanceObserver | null {
    if (typeof PerformanceObserver === 'undefined') {
        return null;
    }

    try {
        const observer = new PerformanceObserver((list) => {
            for (const entry of list.getEntries()) {
                if (entry.duration > 50) { // Long task threshold
                    callback({
                        duration: entry.duration,
                        startTime: entry.startTime,
                        name: entry.name,
                    });
                }
            }
        });

        observer.observe({ entryTypes: ['longtask'] });
        return observer;
    } catch {
        // Long task observer not supported - graceful degradation
        return null;
    }
}

export { THRESHOLDS, getRating, formatMetric };
